import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import Select from "../common/Select";
import Input from "./../common/Input";
import ReportsExpand from "../common/ReportsExpand";
import { getFormsySubmissionsReport, getProgramUnits } from "../services/soar";
import { getFormList, lookupFormInfo } from "./../forms/forms";

function AssessmentReport({ isFavorite, toggleFavorite, ...rest }) {
  const [reportData, setReportData] = useState({
    Company: "",
    ProgramUnit: "",
    Assessment: "",
    BeginDate: "",
    EndDate: "",
  });

  const [loadStatus, setLoadStatus] = useState({});
  const [programUnits, setProgramUnits] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [controllerSignal, setControllerSignal] = useState({});

  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  useEffect(() => {
    const controller = new AbortController();
    setControllerSignal(controller.signal);
    return () => controller.abort();
  }, []);

  const companyOptions = useMemo(() => {
    function onlyUnique(value, index, self) {
      return self.indexOf(value) === index;
    }

    const companies = safyInfo.user.safyInfo.divisions
      .filter((division) => division.Active === "1")
      .map((division) => division.CompanyName)
      .filter(onlyUnique)
      .sort();

    return companies.map((company) => {
      return { _id: company, name: company };
    });
  }, [safyInfo.user.safyInfo.divisions]);

  const programOptions = useMemo(() => {
    return programUnits
      .filter((unit) => {
        try {
          const portalConfig = JSON.parse(unit.sor_safyportalconfig);

          if (!portalConfig?.forms && !portalConfig.assessments) {
            return false;
          }

          return true;
        } catch {
          console.log("BrokenProgramUnitPortalConfig:", unit.ccx_name);
          return false;
        }
      })
      .map((unit) => {
        return {
          _id: unit.ccx_name,
          name: unit.ccx_name,
        };
      });
  }, [programUnits]);

  const assessmentOptions = useMemo(() => {
    if (!reportData.ProgramUnit) {
      return [];
    }

    const selectedUnit = programUnits.filter(
      (unit) => unit.ccx_name === reportData.ProgramUnit
    )[0];

    if (!selectedUnit) {
      return [];
    }

    const unitConfig = JSON.parse(selectedUnit.sor_safyportalconfig);
    const formGroups = unitConfig.forms || [];
    const assessments = unitConfig.assessments || [];
    const forms = formGroups
      .map((group) => {
        const key = Object.keys(group)[0];
        return group[key];
      })
      .flat();
    const options = forms
      .concat(assessments)
      .filter((assessment) => {
        const assessmentInfo = getFormList(assessment);
        if (!assessmentInfo?.formId) {
          return false;
        }

        return true;
      })
      .sort()
      .map((name) => {
        const assessmentInfo = getFormList(name);

        return { name: name, assessmentInfo: assessmentInfo };
      })
      .filter((obj) => obj.assessmentInfo?.formName)
      .map((obj) => {
        return { _id: obj.name, name: obj.assessmentInfo.formName };
      });

    return options;
  }, [reportData.ProgramUnit, programUnits]);

  const filteredSubmissions = useMemo(() => {
    const filtered = submissions.filter((submission) => {
      if (submission.FinalSigned !== "1") {
        return false;
      }

      if (!reportData.BeginDate && !reportData.EndDate) {
        return true;
      }

      const createdOn = moment(submission.CreatedOn).startOf("day");
      const beginDate = moment(reportData.BeginDate).startOf("day");
      const endDate = moment(reportData.EndDate).startOf("day");

      if (
        moment(beginDate).isSameOrBefore(createdOn) &&
        moment(endDate).isSameOrAfter(createdOn)
      ) {
        return true;
      }

      return false;
    });

    return filtered;
  }, [submissions, reportData.BeginDate, reportData.EndDate]);

  const handleChange = ({ currentTarget: input }) => {
    const value = input.type === "checkbox" ? input.checked : input.value;

    console.log("input: ", input, value);
    const data = { ...reportData };
    data[input.name] = value;
    setReportData(data);
  };

  const handleCompanyChange = async ({ currentTarget: input }) => {
    handleChange({ currentTarget: input });

    if (!input.value) {
      return;
    }

    setLoadStatus((ls) => {
      return { ...ls, loadingProgramUnits: true };
    });
    const programUnits = await getProgramUnits(input.value, controllerSignal);

    if (controllerSignal.aborted) {
      return;
    }

    setLoadStatus((ls) => {
      return { ...ls, loadingProgramUnits: false };
    });
    setProgramUnits(programUnits || []);
  };

  const loadContacts = useCallback(
    (programUnit, formName, beginDate, endDate) => {
      setSubmissions([]);
      const loadSubmissions = async () => {
        setLoadStatus((ls) => {
          return { ...ls, loadingSubmissions: true };
        });
        const assessmentInfo = getFormList(formName);
        const submissionResults = await getFormsySubmissionsReport(
          assessmentInfo.formId,
          programUnit,
          moment(beginDate).format("M-D-YYYY"),
          moment(endDate).format("M-D-YYYY")
        );

        if (controllerSignal?.aborted) {
          return;
        }

        if (!submissionResults || submissionResults.length === 0) {
          toast.info("No submissions found");
        } else {
          setSubmissions(
            submissionResults.sort((a, b) => {
              const diff = a.ContactName.localeCompare(b.ContactName);

              if (diff !== 0) {
                return diff;
              } else {
                const aDate = moment(a.CreatedOn).startOf("day");
                const bDate = moment(b.CreatedOn).startOf("day");
                return bDate.diff(aDate);
              }
            })
          );
        }

        setLoadStatus((ls) => {
          return { ...ls, loadingSubmissions: false };
        });
      };

      loadSubmissions();
    },
    [controllerSignal]
  );

  const getReportLink = (submission) => {
    let assessmentName = reportData.Assessment;
    const vIndex = /[v]\d/.exec(assessmentName)?.index;

    if (vIndex) {
      assessmentName = assessmentName.substring(0, vIndex);
    }

    const formConfig = lookupFormInfo(assessmentName, submission.FormVersionId);

    if (!formConfig?.formPath) {
      return;
    }

    const version = parseInt(formConfig.formVersionNumber);

    return `/${formConfig.formPath}${version > 1 ? `v${version}` : ""}/${
      submission.FormVersionId
    }/${submission.ContactId}/${submission.SubmissionId}`;
  };

  return (
    <ReportsExpand
      title="SAFY Assessments/Forms"
      isFavorite={isFavorite}
      toggleFavorite={toggleFavorite}
      {...rest}
    >
      <div className="d-flex flex-column">
        <div className="d-flex flex-column flex-md-row align-items-start mb-3">
          <div className="me-md-3 mb-3 mb-md-0" style={{ minWidth: "200px" }}>
            <label className="form-label">Company</label>
            <Select
              name="Company"
              value={reportData.Company}
              options={companyOptions}
              onChange={handleCompanyChange}
              hideLabel={true}
            />
          </div>

          {!loadStatus.loadingProgramUnits ? (
            <>
              <div
                className="me-md-3 mb-3 mb-md-0"
                style={{ minWidth: "200px" }}
              >
                <label className="form-label">Program Unit</label>
                <Select
                  name="ProgramUnit"
                  value={reportData.ProgramUnit}
                  options={programOptions}
                  onChange={handleChange}
                  hideLabel={true}
                />
              </div>

              {!loadStatus.loadingSubmissions ? (
                <>
                  <div
                    className="me-md-3 mb-3 mb-md-0"
                    style={{ minWidth: "200px" }}
                  >
                    <label className="form-label">Assessment</label>
                    <Select
                      name="Assessment"
                      value={reportData.Assessment}
                      options={assessmentOptions}
                      onChange={handleChange}
                      hideLabel={true}
                    />
                  </div>

                  <div
                    className="me-md-3 mb-3 mb-md-0"
                    style={{ minWidth: "200px" }}
                  >
                    <label className="form-label">Begin Date</label>
                    <Input
                      name="BeginDate"
                      value={reportData.BeginDate}
                      type="date"
                      onChange={handleChange}
                      hideLabel={true}
                    />
                  </div>

                  <div
                    className="me-md-3 mb-3 mb-md-0"
                    style={{ minWidth: "200px" }}
                  >
                    <label className="form-label">End Date</label>
                    <Input
                      name="EndDate"
                      value={reportData.EndDate}
                      type="date"
                      onChange={handleChange}
                      hideLabel={true}
                    />
                  </div>

                  <div className="d-flex align-items-end mb-md-0">
                    {reportData.ProgramUnit &&
                      reportData.Assessment &&
                      reportData.BeginDate &&
                      reportData.EndDate && (
                        <i
                          className="fas fa-arrow-circle-right fa-3x"
                          onClick={() => {
                            loadContacts(
                              reportData.ProgramUnit,
                              reportData.Assessment,
                              reportData.BeginDate,
                              reportData.EndDate
                            );
                          }}
                        ></i>
                      )}
                  </div>
                </>
              ) : (
                <>Loading...</>
              )}
            </>
          ) : (
            <>Loading...</>
          )}
        </div>

        {filteredSubmissions.length > 0 ? (
          <div className="row mt-3">
            <div className="col">
              <div className="container rounded bg-white py-2">
                {filteredSubmissions.map((submission) => {
                  return (
                    <div
                      key={submission.SubmissionId}
                      className="row border-bottom"
                    >
                      <div className="col-10 col-md-11">
                        <Link
                          to={getReportLink(submission)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {submission.ContactName} -{" "}
                          {moment(submission.CreatedOn).format("L")}
                        </Link>
                      </div>
                      <div className="col-2 col-md-1 text-center">
                        <Link
                          to={getReportLink(submission)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="fas fa-folder-open fa-lg"></i>
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </ReportsExpand>
  );
}

export default AssessmentReport;
